import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Check, AlertCircle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};

const TOAST_ICON: Record<ToastType, React.ReactNode> = {
  success: <Check className="w-4 h-4 text-hr-lime" aria-hidden="true" />,
  error: <AlertCircle className="w-4 h-4 text-white" aria-hidden="true" />,
  info: <Info className="w-4 h-4 text-white" aria-hidden="true" />,
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, type: ToastType): void => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
        role="region"
        aria-label="Notifications"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            className={`px-4 py-3 rounded shadow-lg font-medium flex items-center gap-2 min-w-[300px] animate-fade-in-up text-white ${
              toast.type === 'error' ? 'bg-red-600' : 'bg-hr-indigo'
            }`}
          >
            {TOAST_ICON[toast.type]}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
